"use strict";
// SPDX-FileCopyrightText: 2024 Red Bee Media Ltd <https://www.redbeemedia.com/>
//
// SPDX-License-Identifier: MIT
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PlayerCore = void 0;
const sprite_vtt_parser_1 = require("@ericssonbroadcastservices/sprite-vtt-parser");
const lodash_throttle_1 = __importDefault(require("lodash.throttle"));
const js_player_program_service_1 = require("@ericssonbroadcastservices/js-player-program-service");
const js_player_shared_1 = require("@ericssonbroadcastservices/js-player-shared");
const AirPlay_1 = require("./airplay/AirPlay");
const device_1 = require("./device");
const common_1 = require("./device/common");
const AbstractPlayer_1 = require("./players/AbstractPlayer");
const AdInsertionPlayer_1 = require("./players/AdInsertionPlayer");
const AssetPlayer_1 = require("./players/AssetPlayer");
const BasePlayer_1 = require("./players/BasePlayer");
const LowLatencyPlayer_1 = require("./players/LowLatencyPlayer");
const ExposureService_1 = require("./services/ExposureService");
const adsOptionsHelper_1 = require("./utils/adsOptionsHelper");
const data_1 = require("./utils/data");
const defaults_1 = require("./utils/defaults");
const dom_1 = require("./utils/dom");
const FullscreenManager_1 = require("./utils/FullscreenManager");
const helpers_1 = require("./utils/helpers");
const logger_1 = require("./utils/logger");
const MediaSessionManager_1 = require("./utils/MediaSessionManager");
const PictureInPictureManager_1 = require("./utils/PictureInPictureManager");
const EXTERNAL_PREFIX = "external:";
const ClassNames = {
    CONTAINER: "redbee-player-container",
    MEDIA_CONTAINER: "redbee-player-media-container",
    SUBTITLE_CONTAINER: "redbee-player-subtitle-container",
};
class PlayerCore extends js_player_shared_1.EmitterBaseClass {
    constructor(options) {
        var _a;
        super();
        this.spriteCueCache = {};
        this.destroyed = false;
        this.state = Object.assign(Object.assign({}, js_player_shared_1.DefaultPlayerState), { isAirPlaying: false, isAirPlayAvailable: false });
        this.subtitleSizeSet = false;
        (0, logger_1.setLogLevel)((_a = options.logLevel) !== null && _a !== void 0 ? _a : js_player_shared_1.LogLevel.NONE);
        this.deviceAdapter = options.deviceAdapter;
        // Setup DOM
        const { wrapperElement, fullscreenElement } = options;
        const container = document.createElement("div");
        container.className = ClassNames.CONTAINER;
        container.tabIndex = 0;
        const mediaContainer = document.createElement("div");
        mediaContainer.className = ClassNames.MEDIA_CONTAINER;
        container.appendChild(mediaContainer);
        this.videoElement = (0, dom_1.setupVideoElement)();
        mediaContainer.appendChild(this.videoElement);
        this.subtitleContainer = document.createElement("div");
        this.subtitleContainer.className = ClassNames.SUBTITLE_CONTAINER;
        mediaContainer.appendChild(this.subtitleContainer);
        this.mediaContainer = mediaContainer;
        this.container = container;
        this.instanceSettings = {
            playerSDKVersion: typeof "1.9.1" !== "undefined"
                ? "1.9.1"
                : "dev-build",
            initOptions: Object.assign(Object.assign(Object.assign({}, defaults_1.PLAYER_OPTIONS), options), { mediaContainer }),
            isAsset: false,
        };
        if (wrapperElement) {
            wrapperElement.insertBefore(this.container, wrapperElement.firstChild);
            const _fullscreenElement = (fullscreenElement && document.querySelector(fullscreenElement)) ||
                wrapperElement;
            if (_fullscreenElement) {
                this.fullscreenManager = new FullscreenManager_1.FullscreenManager(_fullscreenElement, this.videoElement);
            }
        }
        this.pipManager = new PictureInPictureManager_1.PictureInPictureManager(this.videoElement);
        this.updateSubtitleSize();
        window.addEventListener("resize", (this.throttledUpdateSubtitleSize = (0, lodash_throttle_1.default)(this.updateSubtitleSize.bind(this), 500)));
        if (!options.disableAirPlay) {
            this.setupAirPlay();
        }
    }
    setState(state, emit = true) {
        if (this.destroyed) {
            return;
        }
        this.state = Object.assign(Object.assign({}, this.state), state);
        emit && this.emit(js_player_shared_1.PlayerEvents.STATE_CHANGED, this.state);
        // if the subtitleSize have never been set try to set it
        if (!this.subtitleSizeSet) {
            this.updateSubtitleSize();
        }
    }
    /**
     * Call this method to check if the instance is "alive",
     * if the instance has been destroyed this method will
     * throw an error for you
     */
    checkInstance() {
        if (this.destroyed) {
            const message = "Cannot load on a destroyed instance of the player. Please create a new instance";
            throw new js_player_shared_1.InitError(message, {
                type: js_player_shared_1.ErrorTypes.DESTROYED,
            });
        }
    }
    updateSubtitleSize() {
        const subtitleWidth = this.subtitleContainer.clientWidth;
        if (subtitleWidth) {
            this.subtitleSizeSet = true;
            // Allow the container font-size to be between 0.2em and 2em
            const fontSize = Math.min(2, Math.max(0.2, subtitleWidth / 1000));
            this.subtitleContainer.style.fontSize = `${fontSize}em`;
        }
    }
    setupAirPlay() {
        this.airPlay = new AirPlay_1.AirPlay(this.videoElement);
        this.airPlay.on(AirPlay_1.AirPlayEvent.AVAILABILITY_CHANGED, ({ available }) => {
            this.setState({ isAirPlayAvailable: available });
        });
        this.airPlay.on(AirPlay_1.AirPlayEvent.CONNECTED, () => {
            this.setState({ isAirPlaying: true });
            this.emit(js_player_shared_1.PlayerEvents.AIRPLAY_START, undefined);
        });
        this.airPlay.on(AirPlay_1.AirPlayEvent.DISCONNETED, () => {
            this.setState({ isAirPlaying: true });
            this.emit(js_player_shared_1.PlayerEvents.AIRPLAY_STOP, undefined);
        });
    }
    get Player() {
        var _a;
        const { isAsset, mediaInfo, playResponse } = this.instanceSettings;
        const stitcher = (mediaInfo === null || mediaInfo === void 0 ? void 0 : mediaInfo.ssai) ? (_a = playResponse === null || playResponse === void 0 ? void 0 : playResponse.ads) === null || _a === void 0 ? void 0 : _a.stitcher : undefined;
        if (mediaInfo === null || mediaInfo === void 0 ? void 0 : mediaInfo.lowLatency) {
            return LowLatencyPlayer_1.LowLatencyPlayer;
        }
        if (stitcher === "INTERNAL" || stitcher === "NOWTILUS") {
            return AdInsertionPlayer_1.AdInsertionPlayer;
        }
        if (isAsset) {
            return AssetPlayer_1.AssetPlayer;
        }
        return BasePlayer_1.BasePlayer;
    }
    loadPlayer(startTime) {
        if (!this.player) {
            return;
        }
        this.player.load(startTime);
        this.player.once(js_player_shared_1.PlayerEvents.LOADED, () => {
            var _a;
            if (this.autoplay) {
                (_a = this.play()) === null || _a === void 0 ? void 0 : _a.catch(() => {
                    (0, logger_1.info)("Autoplay detection invalid, failed to autoplay");
                });
            }
        });
    }
    updateMetadata(loadOptions, formats) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            let playResponse;
            let assetMetadata;
            let mediaInfo = {
                isLive: false,
                lowLatency: false,
            };
            const { audioOnly, source, materialProfile } = loadOptions || {};
            const { autoplay, adobePrimetimeToken, businessUnit, customer, exposureBaseUrl: baseUrl, maxResolution, sessionToken, } = this.instanceSettings.initOptions;
            if (sessionToken &&
                customer &&
                businessUnit &&
                baseUrl &&
                !formats &&
                loadOptions &&
                source &&
                !source.includes("://")) {
                const { start, end } = loadOptions.manifest || {};
                const isExternalId = source.startsWith(EXTERNAL_PREFIX);
                const assetId = isExternalId
                    ? source.slice(EXTERNAL_PREFIX.length)
                    : source;
                this.exposureService = new ExposureService_1.ExposureService({
                    customer,
                    businessUnit,
                    baseUrl,
                });
                this.exposureBaseUrl = baseUrl;
                const playRequest = this.exposureService.playRequest({
                    assetId,
                    sessionToken,
                    adsOptions: yield (0, adsOptionsHelper_1.enrichAdsOptions)((loadOptions === null || loadOptions === void 0 ? void 0 : loadOptions.ads) || {}, this.videoElement, yield this.getDevice(), autoplay),
                    audioOnly,
                    adobePrimetimeToken,
                    maxResolution,
                    supportedKeySystem: this.instanceSettings.supportedKeySystem,
                    supportedFormatTypes: (0, helpers_1.getSupportedFormats)(),
                    materialProfile,
                    start,
                    end,
                });
                if (isExternalId) {
                    // must fetch the play response first to get the full id
                    playResponse = yield playRequest;
                    if (!playResponse.assetId) {
                        throw new js_player_shared_1.InitError("Could not fetch information for the given source asset id", { type: js_player_shared_1.ErrorTypes.OPTIONS });
                    }
                    loadOptions.source = playResponse.assetId;
                    assetMetadata = yield this.exposureService.assetMetadata(playResponse.assetId);
                }
                else {
                    // regular asset id can fetch in parallel
                    [playResponse, assetMetadata] = yield Promise.all([
                        playRequest,
                        this.exposureService.assetMetadata(assetId),
                    ]);
                }
                formats = playResponse.formats;
                mediaInfo = {
                    isLive: (_a = playResponse === null || playResponse === void 0 ? void 0 : playResponse.streamInfo) === null || _a === void 0 ? void 0 : _a.live,
                    lowLatency: (assetMetadata === null || assetMetadata === void 0 ? void 0 : assetMetadata.materialType) === "LOW_LATENCY_CHANNEL",
                    ssai: (_b = playResponse === null || playResponse === void 0 ? void 0 : playResponse.streamInfo) === null || _b === void 0 ? void 0 : _b.ssai,
                };
                this.setContractRestrictions(playResponse === null || playResponse === void 0 ? void 0 : playResponse.contractRestrictions);
            }
            Object.assign(this.instanceSettings, {
                playResponse,
                assetMetadata,
                formats,
                isAsset: Boolean(formats),
                loadOptions,
                mediaInfo,
            });
        });
    }
    setContractRestrictions(contractRestrictions) {
        var _a, _b;
        if (contractRestrictions) {
            if (contractRestrictions.airplayEnabled === false) {
                (_a = this.airPlay) === null || _a === void 0 ? void 0 : _a.disable();
            }
            else {
                (_b = this.airPlay) === null || _b === void 0 ? void 0 : _b.enable();
            }
            this.setState({
                contractRestrictions,
            });
        }
    }
    setupProgramService() {
        var _a, _b;
        // if it's nether a linear channel nor having a "parent" channel id. Don't initiate.
        const { assetMetadata, initOptions, playResponse } = this.instanceSettings;
        const { businessUnit, customer, exposureBaseUrl, sessionToken } = initOptions;
        if (!customer ||
            !businessUnit ||
            !exposureBaseUrl ||
            !sessionToken ||
            !assetMetadata ||
            !(playResponse === null || playResponse === void 0 ? void 0 : playResponse.epg) ||
            ((assetMetadata === null || assetMetadata === void 0 ? void 0 : assetMetadata.type) !== "TV_CHANNEL" &&
                !((_a = playResponse === null || playResponse === void 0 ? void 0 : playResponse.streamInfo) === null || _a === void 0 ? void 0 : _a.channelId)))
            return;
        this.programService = new js_player_program_service_1.ProgramService({
            asset: assetMetadata,
            play: playResponse,
            baseUrl: exposureBaseUrl,
            customer,
            businessUnit,
            sessionToken,
        });
        (_b = this.player) === null || _b === void 0 ? void 0 : _b.on(js_player_shared_1.PlayerEvents.TIME_UPDATE, ({ utcCurrentTime }) => { var _a; return (_a = this.programService) === null || _a === void 0 ? void 0 : _a.ping(utcCurrentTime); });
        // Event for entitlement blocks
        this.programService.on(js_player_program_service_1.ProgramServiceEvent.NOT_ENTITLED, (data) => {
            var _a;
            this.emit(js_player_shared_1.PlayerEvents.NOT_ENTITLED, data);
            (_a = this.player) === null || _a === void 0 ? void 0 : _a.stop();
        });
        this.programService.on(js_player_program_service_1.ProgramServiceEvent.BLACKOUT, (data) => {
            this.emit(js_player_shared_1.PlayerEvents.BLACKOUT, data);
        });
        this.programService.on(js_player_program_service_1.ProgramServiceEvent.EMPTY_SLOT, () => {
            this.emit(js_player_shared_1.PlayerEvents.EMPTY_SLOT, undefined);
        });
        // Event for what we're currently watching
        this.programService.on(js_player_program_service_1.ProgramServiceEvent.PROGRAM_CHANGED, ({ currentProgram, upcomingProgram }) => {
            this.instanceSettings.programData = {
                current: currentProgram,
                next: upcomingProgram,
            };
            this.emit(js_player_shared_1.PlayerEvents.PROGRAM_CHANGED, {
                channel: assetMetadata,
                program: currentProgram,
                upnext: upcomingProgram,
            });
        });
    }
    setupPlayerEventListeners() {
        var _a;
        (_a = this.player) === null || _a === void 0 ? void 0 : _a.onAll(({ event, data }) => {
            switch (event) {
                case AbstractPlayer_1.INTERNAL_STATE_CHANGED:
                    // re-emitting PlayerEvents.STATE_CHANGED is done in this.setState()
                    this.setState(data);
                    break;
                default:
                    this.emit(event, data);
                    break;
            }
        });
    }
    getPlayerInfo() {
        var _a, _b;
        const engineName = (_a = this.player) === null || _a === void 0 ? void 0 : _a.getPlayerEngineName();
        const engineVersion = (_b = this.player) === null || _b === void 0 ? void 0 : _b.getPlayerEngineVersion();
        return {
            playerVersion: this.instanceSettings.playerSDKVersion,
            playerEngine: {
                name: engineName,
                version: engineVersion,
            },
        };
    }
    getDevice() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield (0, device_1.getDevice)(this.deviceAdapter);
        });
    }
    getStreamInfo() {
        if (!this.player)
            return;
        return this.player.getStreamInfo();
    }
    getSession() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        const { initOptions, loadOptions, playResponse } = this.instanceSettings;
        const { sessionToken } = initOptions;
        const { playSessionId, requestId, assetId } = playResponse || {};
        return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, (assetId && { assetId })), { initOptions,
            loadOptions,
            sessionToken }), (playResponse && { playSessionId })), (playResponse && { requestId })), { playbackFormat: (_a = this.player) === null || _a === void 0 ? void 0 : _a.getPlaybackFormat(), playerEngine: this.player && {
                name: this.player.getPlayerEngineName(),
                version: this.player.getPlayerEngineVersion(),
            }, autoplay: this.autoplay, locale: initOptions.locale || "en" }), (((_b = playResponse === null || playResponse === void 0 ? void 0 : playResponse.cdn) === null || _b === void 0 ? void 0 : _b.provider) && {
            cdnProvider: (_c = playResponse.cdn) === null || _c === void 0 ? void 0 : _c.provider,
        })), ((playResponse === null || playResponse === void 0 ? void 0 : playResponse.analytics) && {
            analyticsPostInterval: (_d = playResponse.analytics) === null || _d === void 0 ? void 0 : _d.postInterval,
            analyticsBucket: (_e = playResponse.analytics) === null || _e === void 0 ? void 0 : _e.bucket,
            analyticsTag: (_f = playResponse.analytics) === null || _f === void 0 ? void 0 : _f.tag,
            analyticsBaseUrl: (_g = playResponse.analytics) === null || _g === void 0 ? void 0 : _g.baseUrl,
            analyticsPercentage: (_j = (_h = playResponse.analytics) === null || _h === void 0 ? void 0 : _h.percentage) !== null && _j !== void 0 ? _j : 100,
        })), { exposureBaseUrl: this.exposureBaseUrl });
    }
    getAssetInfo() {
        var _a;
        return (_a = this.instanceSettings) === null || _a === void 0 ? void 0 : _a.assetMetadata;
    }
    getProgramInfo() {
        var _a;
        return (_a = this.instanceSettings) === null || _a === void 0 ? void 0 : _a.programData;
    }
    getContractRestrictions() {
        var _a;
        return (_a = this.instanceSettings.playResponse) === null || _a === void 0 ? void 0 : _a.contractRestrictions;
    }
    getContainerElement() {
        return this.container;
    }
    getVideoElement() {
        return this.videoElement;
    }
    getSubtitleContainerElement() {
        return this.subtitleContainer;
    }
    /**
     * Get timeline cuepoints with sprite images showing a snapshot of the
     * content at that point in time.
     * @param  {number}                width  desired width of the image, uses the videoElement width / 4 as default
     * @return {Promise<ISpriteCue[]>}
     */
    getTimelineSpriteCues(width) {
        var _a;
        width = width || this.videoElement.offsetWidth / 4;
        const sprites = (_a = this.instanceSettings.playResponse) === null || _a === void 0 ? void 0 : _a.sprites;
        if (sprites === null || sprites === void 0 ? void 0 : sprites.length) {
            let offset = 0;
            let vtt;
            for (const sprite of sprites) {
                vtt = sprite.vtt;
                if (sprite.offsetInMs) {
                    offset = sprite.offsetInMs / 1000;
                }
                if (sprite.vtt && width <= sprite.width) {
                    break;
                }
            }
            if (vtt) {
                return (this.spriteCueCache[vtt] ||
                    (this.spriteCueCache[vtt] = (0, sprite_vtt_parser_1.getTimelineSpriteCues)(vtt, offset)));
            }
        }
        return Promise.resolve([]);
    }
    getState() {
        return this.state;
    }
    getCurrentTime() {
        var _a;
        return (_a = this.player) === null || _a === void 0 ? void 0 : _a.getCurrentTime();
    }
    getVolume() {
        var _a;
        return (_a = this.player) === null || _a === void 0 ? void 0 : _a.getVolume();
    }
    _load(options, formats) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const { autoplay, muted } = this.instanceSettings.initOptions;
            this.emit(js_player_shared_1.PlayerEvents.LOAD_START, undefined);
            this.setState({ playbackState: js_player_shared_1.PlaybackState.LOADING });
            // Only need to check once, since initOptions stays the same
            if (this.autoplay === undefined) {
                this.autoplay = autoplay ? yield (0, helpers_1.isAutoplaySupported)(muted) : false;
            }
            if (options === null || options === void 0 ? void 0 : options.poster) {
                this.videoElement.setAttribute("poster", options.poster);
            }
            else {
                this.videoElement.removeAttribute("poster");
            }
            this.checkInstance();
            this.instanceSettings.supportedKeySystem = yield (0, helpers_1.getSupportedDrm)(this.instanceSettings.initOptions.keysystem);
            this.checkInstance();
            this.emit(js_player_shared_1.PlayerEvents.PLAYER_SETUP_COMPLETED, undefined);
            yield this.updateMetadata(options, formats);
            this.checkInstance();
            this.emit(js_player_shared_1.PlayerEvents.ENTITLEMENT_GRANTED, undefined);
            this.player = new this.Player(this.videoElement, this.subtitleContainer, this.instanceSettings, options === null || options === void 0 ? void 0 : options.source);
            this.setupPlayerEventListeners();
            this.setupProgramService();
            this.emit(js_player_shared_1.PlayerEvents.LOADING, Object.assign({}, (((_a = this.instanceSettings.playResponse) === null || _a === void 0 ? void 0 : _a.playSessionId) && {
                playSessionId: this.instanceSettings.playResponse.playSessionId,
            })));
            if (this.instanceSettings.assetMetadata) {
                new MediaSessionManager_1.MediaSessionManager(this.player, this.instanceSettings);
            }
            this.loadPlayer((_b = options === null || options === void 0 ? void 0 : options.startTime) !== null && _b !== void 0 ? _b : (0, data_1.getStartTime)(this.instanceSettings.playResponse));
        });
    }
    load(options) {
        this._load(options).catch((error) => {
            this.setState({ playbackState: js_player_shared_1.PlaybackState.ERROR });
            this.emit(js_player_shared_1.PlayerEvents.ERROR, error);
        });
    }
    // Preloaded on the server side
    loadFromFormats(formats, options) {
        this._load(options, formats).catch((error) => {
            this.setState({ playbackState: js_player_shared_1.PlaybackState.ERROR });
            this.emit(js_player_shared_1.PlayerEvents.ERROR, error);
        });
    }
    play() {
        var _a;
        return (_a = this.player) === null || _a === void 0 ? void 0 : _a.play();
    }
    getSeekable() {
        var _a;
        return (_a = this.player) === null || _a === void 0 ? void 0 : _a.getSeekable();
    }
    pause() {
        var _a;
        (_a = this.player) === null || _a === void 0 ? void 0 : _a.pause();
    }
    setMuted(muted) {
        var _a;
        (_a = this.player) === null || _a === void 0 ? void 0 : _a.setMuted(muted);
    }
    toggleMuted() {
        var _a;
        (_a = this.player) === null || _a === void 0 ? void 0 : _a.toggleMuted();
    }
    clickThrough() {
        var _a;
        (_a = this.player) === null || _a === void 0 ? void 0 : _a.clickThrough();
    }
    isFullscreen() {
        if (this.fullscreenManager) {
            return this.fullscreenManager.isFullscreen();
        }
    }
    toggleFullscreen() {
        if (this.fullscreenManager) {
            this.fullscreenManager.toggleFullscreen();
        }
    }
    isPictureInPicture() {
        var _a;
        return (_a = this.pipManager) === null || _a === void 0 ? void 0 : _a.isPictureInPicture();
    }
    isPictureInPictureAvailable() {
        var _a;
        return (_a = this.pipManager) === null || _a === void 0 ? void 0 : _a.isPictureInPictureAvailable();
    }
    togglePictureInPicture() {
        var _a;
        return (_a = this.pipManager) === null || _a === void 0 ? void 0 : _a.togglePictureInPicture();
    }
    isAirplaySupported() {
        var _a, _b;
        return (_b = (_a = this.airPlay) === null || _a === void 0 ? void 0 : _a.isSupported()) !== null && _b !== void 0 ? _b : false;
    }
    isAirplaying() {
        var _a, _b;
        return (_b = (_a = this.state) === null || _a === void 0 ? void 0 : _a.isAirPlaying) !== null && _b !== void 0 ? _b : false;
    }
    toggleAirPlay() {
        var _a;
        return (_a = this.airPlay) === null || _a === void 0 ? void 0 : _a.toggleAirPlay();
    }
    seekTo(time) {
        var _a;
        (_a = this.player) === null || _a === void 0 ? void 0 : _a.seekTo(time);
    }
    seekToUTC(utcTime) {
        var _a;
        let seekToSec = (utcTime - this.state.utcSeekable.start) / 1000;
        if (this.state.seekable.start)
            seekToSec += this.state.seekable.start;
        (_a = this.player) === null || _a === void 0 ? void 0 : _a.seekTo(seekToSec);
    }
    scrub(offset) {
        var _a;
        (_a = this.player) === null || _a === void 0 ? void 0 : _a.scrub(offset);
    }
    seekToLive() {
        var _a;
        if (this.isLive()) {
            (_a = this.player) === null || _a === void 0 ? void 0 : _a.seekToLive();
        }
    }
    setPlaybackRate(rate) {
        var _a;
        if (this.player) {
            (_a = this.player) === null || _a === void 0 ? void 0 : _a.setPlaybackRate(rate);
        }
    }
    setQualityLevel(level) {
        var _a;
        if (this.player) {
            (_a = this.player) === null || _a === void 0 ? void 0 : _a.setQualityLevel(level);
        }
    }
    getQualityLevels() {
        var _a;
        if (this.player) {
            return (_a = this.player) === null || _a === void 0 ? void 0 : _a.getQualityLevels();
        }
        return [];
    }
    setAudioTrack(track) {
        var _a;
        (_a = this.player) === null || _a === void 0 ? void 0 : _a.setAudioTrack(track);
    }
    setSubtitleTrack(track) {
        var _a;
        (_a = this.player) === null || _a === void 0 ? void 0 : _a.setSubtitleTrack(track);
    }
    setVolume({ percentage }) {
        var _a;
        (_a = this.player) === null || _a === void 0 ? void 0 : _a.setVolume({ percentage });
    }
    getAudioTrack() {
        var _a;
        return (_a = this.player) === null || _a === void 0 ? void 0 : _a.getAudioTrack();
    }
    getAudioTracks() {
        var _a, _b;
        return (_b = (_a = this.player) === null || _a === void 0 ? void 0 : _a.getAudioTracks()) !== null && _b !== void 0 ? _b : [];
    }
    getSubtitleTrack() {
        var _a;
        return (_a = this.player) === null || _a === void 0 ? void 0 : _a.getSubtitleTrack();
    }
    getSubtitleTracks() {
        var _a, _b;
        return (_b = (_a = this.player) === null || _a === void 0 ? void 0 : _a.getSubtitleTracks()) !== null && _b !== void 0 ? _b : [];
    }
    isLive() {
        var _a;
        let live = (_a = this.player) === null || _a === void 0 ? void 0 : _a.isLive();
        // If the player engine hasn't been initialized yet, we'll use the mediaInfo to determine if it's a live stream.
        // This so that we can initialize analytics with the correct live state.
        if (live === undefined && this.instanceSettings.mediaInfo) {
            live = this.instanceSettings.mediaInfo.isLive;
        }
        return live;
    }
    isPlaying() {
        var _a, _b;
        return (_b = (_a = this.player) === null || _a === void 0 ? void 0 : _a.isPlaying()) !== null && _b !== void 0 ? _b : false;
    }
    // is the current browser/device supported
    // We should probably try to deprecate this for the static one, but it's used in PlayerCoreConnector
    isBrowserSupported() {
        return (0, common_1.isBrowserSupported)();
    }
    // same as above but exposed before an instance is created
    static isBrowserSupported() {
        return (0, common_1.isBrowserSupported)();
    }
    isVolumeReadOnly() {
        return (0, helpers_1.isVolumeReadOnly)();
    }
    isPictureInPictureSupported() {
        return PictureInPictureManager_1.PictureInPictureManager.isSupported();
    }
    static isPictureInPictureSupported() {
        return PictureInPictureManager_1.PictureInPictureManager.isSupported();
    }
    destroy() {
        var _a, _b, _c;
        this.destroyed = true;
        (_a = this.pipManager) === null || _a === void 0 ? void 0 : _a.destroy();
        (_b = this.player) === null || _b === void 0 ? void 0 : _b.destroy();
        (_c = this.programService) === null || _c === void 0 ? void 0 : _c.destroy();
        this.container.remove();
        window.removeEventListener("resize", this.throttledUpdateSubtitleSize);
        super.destroy();
    }
}
exports.PlayerCore = PlayerCore;
//# sourceMappingURL=PlayerCore.js.map